import { useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api';
import type { Role, SessionUser } from '../api';
import {
  ArchiveButton,
  ArchivedToggle,
  Button,
  Card,
  Empty,
  ErrorBanner,
  Field,
  Modal,
  ModalActions,
  PageHeadRow,
  Pill,
  TableScroll,
  formatDate,
  useAction,
  useLoad,
} from '../ui';
import { PasswordField } from '../password';
import { usePlainLanguage } from '../plainLanguage';
import { ResetPasswordButton } from '../ResetPassword';

/**
 * The logins belonging to one organization.
 *
 * Staff logins belong to no organization and so never appear here — they have their
 * own page, and there is no screen that lists every customer's users at once, which is
 * the flat view this page used to be.
 */
export function UsersSection({ user, orgId }: { user: SessionUser; orgId: string }) {
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);

  const users = useLoad(() => api.users.list(orgId, showArchived), [orgId, showArchived]);
  const action = useAction();
  const plain = usePlainLanguage();

  return (
    <>
      <div className="mb-3.5">
        <PageHeadRow
          actions={
            <>
              <ArchivedToggle showArchived={showArchived} onChange={setShowArchived} />
              <Button onClick={() => setAdding(true)}>Add user</Button>
            </>
          }
        >
          <p className="m-0 max-w-[70ch] text-muted">
            {plain
              ? 'Everyone who can sign in here to look after your shops. Counters do not need a login.'
              : 'Logins for this console. Counters do not use these — they pair instead.'}
          </p>
        </PageHeadRow>
      </div>

      <ErrorBanner message={users.error ?? action.error} />

      <Modal open={adding} title="Add a user" onClose={() => setAdding(false)}>
        <NewUser
          orgId={orgId}
          onCreated={() => {
            setAdding(false);
            users.reload();
          }}
        />
      </Modal>

      <Card>
        {users.loading && <Empty>Loading…</Empty>}
        {!users.loading && users.data?.length === 0 && (
          <Empty>{showArchived ? 'No users, archived or otherwise.' : 'No users yet.'}</Empty>
        )}

        {users.data && users.data.length > 0 && (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last signed in</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data.map((row) => (
                  <tr key={row.id}>
                    <td>{row.email}</td>
                    <td>{describeRole(row.role)}</td>
                    <td>
                      <Pill tone={row.status === 'active' ? 'on' : 'warn'}>{row.status}</Pill>
                    </td>
                    <td className="text-muted">{formatDate(row.lastLoginAt)}</td>
                    <td className="text-right">
                      {/* Neither is offered on your own login: archiving it would lock you
                          out mid-session, and your own password is changed from the account
                          menu. The server refuses both too. */}
                      {row.id !== user.id && (
                        <div className="inline-flex flex-wrap items-center justify-end gap-2">
                          {/* An archived login cannot sign in, so a reset would do nothing. */}
                          {row.status === 'active' && (
                            <ResetPasswordButton
                              userId={row.id}
                              email={row.email}
                              busy={action.busy}
                              run={action.run}
                            />
                          )}
                          <ArchiveButton
                            status={row.status}
                            label={row.email}
                            cascadeNote="They will not be able to sign in."
                            busy={action.busy}
                            onArchive={() =>
                              void action.run(() => api.users.archive(row.id)).then(users.reload)
                            }
                            onRestore={() =>
                              void action.run(() => api.users.restore(row.id)).then(users.reload)
                            }
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Card>
    </>
  );
}

function NewUser({ orgId, onCreated }: { orgId: string; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const action = useAction();

  async function create(event: FormEvent) {
    event.preventDefault();
    const ok = await action.run(() =>
      // Every customer login administers its own organization; there is no lesser role.
      api.users.create({ email: email.trim(), password, role: 'customer_admin', orgId }),
    );
    if (ok) {
      setEmail('');
      setPassword('');
      onCreated();
    }
  }

  return (
    <form onSubmit={create} className="flex flex-col gap-3">
      <ErrorBanner message={action.error} />
      <Field label="Email">
        <input
          type="email"
          required
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>
      <Field label="Password" hint="At least 8 characters. Generate one, copy it, and hand it over.">
        <PasswordField value={password} onChange={setPassword} />
      </Field>
      <ModalActions>
        <Button type="submit" disabled={action.busy}>
          {action.busy ? 'Creating…' : 'Create user'}
        </Button>
      </ModalActions>
    </form>
  );
}

function describeRole(role: Role): string {
  switch (role) {
    case 'staff':
      return 'Sorting Company staff';
    case 'customer_admin':
      return 'Administrator';
  }
}
